import React, {useEffect, useState} from "react";
import {
    Stack,
    StackItem,
    Label,
    PrimaryButton,
    Dialog,
    DialogFooter,
    DialogType,
    DefaultButton,
    MessageBarType
} from "@fluentui/react";
import { useAuth } from "react-oidc-context";
import { displayMessageBar } from "../LayoutWithAuth/LayoutWithAuth";
import {
    getTotalAssessmentsForDeletion,
    bulkDeleteAssessments,
    downloadAssessmentsForDeletion
} from "../../services/ManageSystemServices/BulkDeleteAssessmentsService";
import { FnpDatePicker, stringToDate, DatePickerPropsOut } from "../Utils";
import strings from "../../loc/strings.ts";
import EiopaFnPIcons from "../EiopaFnPIcons/EiopaFnPIcons.tsx";

const datePickerStyle = {
    root: {
        selectors: {
            '& div[class*="statusMessage-"]': {
                marginTop: "0px",
            },
            '& div[role="alert"]': {
                position: "absolute",
            },
        },
    },
    statusMessage: {
        display: 'none'
    }
};

const BulkDeleteAssessments: React.FC = () => {
    const auth = useAuth();
    const [fromDate, setFromDate] = useState<{ value?: string; isValid: boolean }>({ value: undefined, isValid: true });
    const [toDate, setToDate] = useState<{ value?: string; isValid: boolean }>({ value: undefined, isValid: true });
    const [resetFlag, setResetFlag] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [totalToDelete, setTotalToDelete] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);

    const handleFromDateChange = (datePickerProps: DatePickerPropsOut) => {
        setFromDate({
            value: datePickerProps.value,
            isValid: datePickerProps.valid
        });
    };

    const handleToDateChange = (datePickerProps: DatePickerPropsOut) => {
        setToDate({
            value: datePickerProps.value,
            isValid: datePickerProps.valid
        });
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return "";
        const date = stringToDate(dateStr);
        if (!date) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const handleConfirm = async () => {
        if (!fromDate.value || !toDate.value) {
            displayMessageBar(MessageBarType.error, strings.messageBarBulkDeleteAssessmentsSelectBothDates, false);
            return;
        }

        const from = stringToDate(fromDate.value);
        const to = stringToDate(toDate.value);

        if (from && to && from > to) {
            displayMessageBar(MessageBarType.error, strings.messageBarBulkDeleteAssessmentsFromDateAfterToDate, false);
            return;
        }

        setLoading(true);

        try {
            const token = auth.user?.id_token ?? "";
            const data = await getTotalAssessmentsForDeletion(
                formatDate(fromDate.value),
                formatDate(toDate.value),
                token
            );
            setTotalToDelete(data.totalNumberOfAssessmentsToBeDeleted);
            if (data.totalNumberOfAssessmentsToBeDeleted === 0) {
                displayMessageBar(MessageBarType.warning, strings.messageBarBulkDeleteAssessmentsNoAssessmentsFound, false);
                setLoading(false);
                return;
            }
            setModalOpen(true);
        } catch (e: any) {
            displayMessageBar(MessageBarType.error, "Failed to fetch count.", false);
        }
        setLoading(false);
    };

    const onReset = () => {
        setFromDate({ value: undefined, isValid: true });
        setToDate({ value: undefined, isValid: true });
        setResetFlag(!resetFlag);
        setTotalToDelete(null);
        setModalOpen(false);
    };

    const handleDownloadExcel = async () => {
        if (!fromDate.value || !toDate.value) return;
        
        setLoading(true);
        try {
            const token = auth.user?.id_token ?? "";
            const blob = await downloadAssessmentsForDeletion(
                formatDate(fromDate.value),
                formatDate(toDate.value),
                token
            );
            
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Assessments_for_Deletion_${formatDate(fromDate.value)}_to_${formatDate(toDate.value)}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            displayMessageBar(MessageBarType.success, "Excel file downloaded successfully.", false);
        } catch (e: any) {
            displayMessageBar(MessageBarType.error, "Failed to download Excel file.", false);
        }
        setLoading(false);
    };

    const handleBulkDelete = async () => {
        setLoading(true);
        try {
            const token = auth.user?.id_token ?? "";
            const response = await bulkDeleteAssessments(
                formatDate(fromDate.value),
                formatDate(toDate.value),
                token
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMsg = errorData?.message || "Failed to delete assessments.";
                displayMessageBar(MessageBarType.error, errorMsg, false);
            } else {
                displayMessageBar(MessageBarType.success, strings.messageBarBulkDeleteAssessmentsSuccess, false);
            }
        } catch (e: any) {
            displayMessageBar(MessageBarType.error, "Failed to delete assessments.", false);
        }
        setLoading(false);
        setModalOpen(false);
    };

    if (!auth.user || !auth.isAuthenticated) {
        return null;
    }

    return (
        <>
            <Stack tokens={{ childrenGap: 10 }} >
                <StackItem>
                    <Label style={{ fontSize: 20, fontWeight: 600 }}>
                        {strings.bulkDeleteAssessmentsPageHeader}
                    </Label>
                </StackItem>

                <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="end" wrap>
                    <StackItem styles={{ root: { minWidth: 350 } }}>
                        <FnpDatePicker
                            datePickerKey={`bulkDeleteFrom-${resetFlag}`}
                            isRequired={true}
                            date={fromDate.value ? stringToDate(fromDate.value) || undefined : undefined}
                            resetFlag={resetFlag}
                            resetInitDate={true}
                            setSelectedDate={handleFromDateChange}
                            initialPickerDate={fromDate.value ? stringToDate(fromDate.value) || new Date() : new Date()}
                            label="Date of assessment (from)"
                            placeholder="DD/MM/YYYY"
                            isOutOfBoundsErrorMessage="Date must be before or equal to To Date"
                            styles={datePickerStyle}
                        />
                    </StackItem>
                    <StackItem styles={{ root: { minWidth: 350 } }}>
                        <FnpDatePicker
                            datePickerKey={`bulkDeleteTo-${resetFlag}`}
                            isRequired={true}
                            date={toDate.value ? stringToDate(toDate.value) || undefined : undefined}
                            resetFlag={resetFlag}
                            resetInitDate={true}
                            setSelectedDate={handleToDateChange}
                            initialPickerDate={toDate.value ? stringToDate(toDate.value) || new Date() : new Date()}
                            label="Date of assessment (to)"
                            placeholder="DD/MM/YYYY"
                            isOutOfBoundsErrorMessage="Date must be after or equal to From Date"
                            styles={datePickerStyle}
                        />
                    </StackItem>
                    <StackItem>
                        <PrimaryButton
                            text="Find"
                            onClick={handleConfirm}
                            disabled={loading}
                            styles={{ root: { height: 30, padding: '0 24px' } }}
                        />
                    </StackItem>
                    <StackItem>
                        <DefaultButton
                            text="Reset"
                            onClick={onReset}
                            disabled={loading}
                            styles={{ root: { height: 30, padding: '0 24px' } }}
                        />
                    </StackItem>
                </Stack>
            </Stack>

            <Dialog
                hidden={!modalOpen}
                minWidth="310px"
                maxWidth="310px"
                onDismiss={() => setModalOpen(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: strings.bulkDeleteAssessmentsDialogHeader,
                    subText: `You are about to delete ${totalToDelete} assessments from ${fromDate.value ? stringToDate(fromDate.value)?.toLocaleDateString("fr-BE") || "" : ""} to ${toDate.value ? stringToDate(toDate.value)?.toLocaleDateString("fr-BE") || "" : ""}. This action is irreversible. Are you sure you want to continue?`
                }}
            >
                <Stack tokens={{ childrenGap: 10 }} style={{ marginBottom: 20 }}>

                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            handleDownloadExcel();
                        }}
                        style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            textDecoration: 'none',
                            color: '#0078d4',
                            fontSize: '14px',
                            cursor: loading ? 'default' : 'pointer',
                            opacity: loading ? 0.5 : 1,
                            pointerEvents: loading ? 'none' : 'auto'
                        }}
                    >
                        <EiopaFnPIcons iconName={"download"} iconColor={"#0078D4"} />
                        Download Excel File
                    </button>
                </Stack>
                <DialogFooter styles={{ actions: { justifyContent: 'flex-start', display: 'flex', gap: '10px', margin: '0px' } }}>
                    <PrimaryButton 
                        text="Confirm deletion" 
                        onClick={handleBulkDelete} 
                        disabled={loading}
                        styles={{ root: { marginRight: '8px' } }}
                    />
                    <DefaultButton text="Cancel" onClick={() => setModalOpen(false)} />
                </DialogFooter>
            </Dialog>
        </>
    );
};

export default BulkDeleteAssessments;
